import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import { BASE_URL } from "../../../const";

const TaskSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const layer = params.get("layer");
  const plantSku = params.get("sku");
  const plantNameQuery = params.get("plantName") || "";

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [totalQty, setTotalQty] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isPendingSubmission, setIsPendingSubmission] = useState(false);
  const [plantName, setPlantName] = useState(plantNameQuery);
  const [warehouse, setWarehouse] = useState(undefined);

  // Resolve warehouse once
  useEffect(() => {
    const selected = localStorage.getItem("selectedWarehouse");
    if (selected) {
      setWarehouse(selected);
      return;
    }
    const store = localStorage.getItem("store");
    if (store === "SELECT BRANDS (I) PVT. LTD. (PUNE)") {
      setWarehouse("pune");
      return;
    }
    if (store === "SELECT BRANDS (I) PVT. LTD. (KOLKATA)") {
      setWarehouse("kolkata");
      return;
    }
    setWarehouse(undefined);
  }, []);

  // Read pending localStorage (runs when layer/plantSku/plantName change)
  useEffect(() => {
    const existingData = localStorage.getItem(
      `pending_batch_layer_${layer}_${plantSku}`
    );
    if (existingData) {
      setIsPendingSubmission(true);
      try {
        const parsed = JSON.parse(existingData);
        if (parsed?.plantName && !plantName) {
          setPlantName(parsed.plantName);
        }
      } catch (error) {
        console.warn("Invalid pending batch data:", error);
      }
    } else {
      setIsPendingSubmission(false);
    }
  }, [layer, plantSku, plantName]);

  // fetchTasks reads warehouse from state but we only call it when warehouse is defined
  const fetchTasks = async () => {
    if (!warehouse) return;
    const token = localStorage.getItem("token");
    try {
      setLoading(true);

      const qs = new URLSearchParams();
      qs.set("status", "pending");
      if (layer) qs.set("stage", layer);
      if (plantSku) qs.set("sku", plantSku);
      if (startDate) qs.set("start_date", startDate);
      if (endDate) qs.set("end_date", endDate);
      qs.set("warehouse", warehouse);

      const url = `${BASE_URL}/tasks/filter/?${qs.toString()}`;

      const res = await fetch(url, {
        headers: {
          Authorization: "Token " + token,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.error("Failed to fetch tasks:", res.status, txt);
        setTasks([]);
        return;
      }

      const data = await res.json();
      // normalize array response
      const items = Array.isArray(data) ? data : data.data || data.results || data.items || [];
      setTasks(items);

      if (!plantName && items?.length) {
        const derivedName = items[0].sku_name || items[0].plant_name || "";
        if (derivedName) setPlantName(derivedName);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Call fetchTasks when warehouse, layer, startDate, endDate, or plantSku change
  useEffect(() => {
    if (!warehouse) {
      // optionally show a message / skip fetch
      setTasks([]);
      setLoading(false);
      return;
    }
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouse, layer, startDate, endDate, plantSku]);

  const handleSelectTask = (taskId, qty) => {
    let newSelected;
    if (selectedTasks.includes(taskId)) {
      newSelected = selectedTasks.filter((id) => id !== taskId);
      setTotalQty((prev) => prev - qty);
    } else {
      newSelected = [...selectedTasks, taskId];
      setTotalQty((prev) => prev + qty);
    }
    setSelectedTasks(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === tasks.length) {
      setSelectedTasks([]);
      setTotalQty(0);
    } else {
      setSelectedTasks(tasks.map((t) => t.task_id));
      const sumQty = tasks.reduce((sum, t) => sum + (t.qty || 0), 0);
      setTotalQty(sumQty);
    }
  };

  const goToBatchSubmission = () => {
    const searchParams = new URLSearchParams({
      layer: layer || "",
      sku: plantSku || "",
      plantName: plantName || "",
    });
    navigate(`/batch-submission?${searchParams.toString()}`);
  };

  const handleNext = () => {
    const batchData = {
      layer,
      plantSku,
      plantName,
      totalQty,
      taskIds: selectedTasks,
      warehouse: warehouse, // use resolved warehouse
      status: "pending",
    };
    localStorage.setItem(
      `pending_batch_layer_${layer}_${plantSku}`,
      JSON.stringify(batchData)
    );
    goToBatchSubmission();
  };


  return (
    <>
      <Navbar />
      <div className="p-4 max-w-7xl mx-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        {/* Header Section */}
{/* Header Section */}
<div className="mb-4">
<div className="flex items-center justify-between mb-3">

  {/* LEFT: Back Button + Title */}
  <div className="flex items-center gap-3">

    {/* 🔙 Back Button */}
    <button
      onClick={() => navigate(`/layer/${layer}`)}
      className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 
                 transition border border-gray-300 flex items-center justify-center"
      title="Go Back"
    >
      <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    {/* Title + Tags */}
    <div>
      <h2 className="text-xl font-bold text-gray-900">
        Task Selection
      </h2>

      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600">Layer</span>
          <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-md border border-blue-200">
            {layer}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600">Plant</span>
          <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded-md border border-purple-200">
            {plantName || "—"}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600">SKU</span>
          <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-md border border-green-200">
            {plantSku}
          </span>
        </div>
      </div>
    </div>
  </div>

</div>


  {/* Pending Batch Button */}
  {isPendingSubmission && (
    <button
      onClick={goToBatchSubmission}
      className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2 text-sm font-medium mb-3"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Complete Pending Batch
    </button>
  )}

  {/* Compact Date Filters */}
  <div className="bg-white rounded-lg p-3 border border-gray-200">
  <div className="flex flex-wrap items-end gap-2">

    <div className="flex flex-col">
      <label className="text-[10px] font-medium text-gray-600 mb-0.5">From</label>
      <input
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        className="w-[115px] border border-gray-300 px-2 py-1 rounded text-xs"
      />
    </div>

    <div className="flex flex-col">
      <label className="text-[10px] font-medium text-gray-600 mb-0.5">To</label>
      <input
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        className="w-[115px] border border-gray-300 px-2 py-1 rounded text-xs"
      />
    </div>

    <button
      onClick={fetchTasks}
      className="bg-gray-700 text-white px-3 py-2 text-xs rounded hover:bg-gray-800 transition"
    >
      Apply
    </button>

  </div>
</div>

</div>

        {/* Summary Stats */}
        {!loading && tasks.length > 0 && (
          <div className="bg-white rounded-lg p-3 border border-gray-200 mb-3 grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-xs text-gray-600 mb-1">Total Tasks</div>
              <div className="text-lg font-bold text-gray-900">{tasks.length}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">Selected</div>
              <div className="text-lg font-bold text-green-600">{selectedTasks.length}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">Total Qty</div>
              <div className="text-lg font-bold text-orange-600">{totalQty.toLocaleString()}</div>
            </div>
          </div>
        )}

        {/* Tasks Section */}
        {loading ? (
          <div className="flex justify-center items-center h-40 bg-white rounded-lg border border-gray-200">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading tasks...</p>
            </div>
          </div>
        ) : tasks.length === 0 ? (
          <div className="bg-white rounded-lg p-6 text-center border border-gray-200">
            <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m0 0V9m0 8h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-600 mt-2">No tasks found</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Header with Select All */}
            <div className="bg-gray-50 p-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Task List</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedTasks.length === tasks.length && tasks.length > 0}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-xs font-medium text-gray-700">Select All</span>
              </label>
            </div>

            {/* Tasks List */}
{/* Tasks List - Ultra Compact */}
        <div className="overflow-y-auto" style={{ maxHeight: "50vh" }}>
          <div className="divide-y divide-gray-200">
            {tasks.map((t) => {
              const isSelected = selectedTasks.includes(t.task_id);

              return (
                <div
                  key={t.task_id}
                  onClick={() => handleSelectTask(t.task_id, t.qty)}   // 👈 ROW CLICKABLE
                  className={`p-2 cursor-pointer transition ${
                    isSelected ? "bg-blue-50 border-l-2 border-blue-500" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2">

                    {/* CHECKBOX — stop row click from firing twice */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onClick={(e) => e.stopPropagation()}   // 👈 prevents row onClick
                      onChange={() => handleSelectTask(t.task_id, t.qty)}
                      className="w-4 h-4 text-blue-600 rounded flex-shrink-0"
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">

                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {t.date || "No date"}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-bold text-gray-900">
                            {t.qty.toLocaleString()}
                          </span>
                          <span
                            className={`w-2 h-2 rounded-full ${
                              t.status === "pending" ? "bg-orange-500" : "bg-green-500"
                            }`}
                          ></span>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

          </div>
        )}

        {/* Action Button */}
        {selectedTasks.length > 0 && !isPendingSubmission && (
          <div className="mt-4 bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Selected Quantity</p>
                <p className="text-lg font-bold text-gray-900">{totalQty.toLocaleString()}</p>
              </div>
              <button
                onClick={handleNext}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-semibold text-sm flex items-center gap-2"
              >
                <span>Continue</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TaskSelectionPage;